import { useEffect, useState } from "react";
import * as api from "../lib/api";

export function ProjectList({ onOpen }: { onOpen: (projectId: string) => void }) {
  const [projects, setProjects] = useState<api.Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  async function refresh() {
    try {
      const r = await api.listProjects();
      setProjects(r.projects);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  // Initial load + a couple of silent auto-retries so transient backend restarts
  // (e.g. tsx watch racing on port 4000) recover without a user click.
  useEffect(() => {
    let cancelled = false;
    async function tryLoad(attempt: number) {
      if (cancelled) return;
      try {
        const r = await api.listProjects();
        if (cancelled) return;
        setProjects(r.projects);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        if (attempt < 3) {
          setTimeout(() => void tryLoad(attempt + 1), 1500);
        } else {
          setError((e as Error).message);
        }
      }
    }
    void tryLoad(0);
    return () => {
      cancelled = true;
    };
  }, []);

  async function createNew() {
    setCreating(true);
    try {
      const r = await api.createProject("Untitled app");
      onOpen(r.project.id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function onImportPicked(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = ""; // allow re-picking the same file later
    if (!file) return;
    setCreating(true);
    try {
      const r = await api.importProject(file);
      onOpen(r.project.id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function onDelete(id: string, ev: React.MouseEvent) {
    ev.stopPropagation();
    if (!confirm("Delete this project? The deployed URL will stop working.")) return;
    try {
      await api.deleteProject(id);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="project-list">
      <h1>Your apps</h1>
      <div className="sub">
        Quikpl micro-apps stay live at <span className="mono">quikpl.ai/a/&lt;id&gt;</span>. Build one, share the
        URL or QR, redeploy as often as you want — the link never changes.
      </div>
      <div style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
        <button className="primary" onClick={createNew} disabled={creating}>
          {creating ? "Creating…" : "+ New app"}
        </button>
        <label
          className="btn-ghost"
          style={{ cursor: creating ? "not-allowed" : "pointer", opacity: creating ? 0.5 : 1 }}
          title="Upload a Quikpl project zip (exported from any Quikpl deployment)"
        >
          ⬆ Import
          <input
            type="file"
            accept=".zip,application/zip"
            disabled={creating}
            onChange={onImportPicked}
            style={{ display: "none" }}
          />
        </label>
      </div>
      {error && (
        <div className="mono" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <span className="status-err">Couldn't reach backend: {error}</span>
          <button onClick={() => { setError(null); void refresh(); }}>↻ Retry</button>
        </div>
      )}
      {projects === null && !error ? (
        <div className="status-warn"><span className="spinner" /> loading…</div>
      ) : projects && projects.length === 0 ? (
        <div className="empty">
          You haven't built anything yet. Click <strong>+ New app</strong> above to start.
        </div>
      ) : projects ? (
        projects.map((p) => (
          <div key={p.id} className="project-card" onClick={() => onOpen(p.id)}>
            <div className="name">{p.name || "Untitled"}</div>
            <div className="meta mono" style={{ marginRight: 12 }}>
              {p.id} · updated {ago(p.updatedAt)}
            </div>
            <button className="btn-ghost danger" onClick={(e) => onDelete(p.id, e)}>delete</button>
          </div>
        ))
      ) : null}
    </div>
  );
}

function ago(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return s + "s ago";
  const m = Math.floor(s / 60);
  if (m < 60) return m + "m ago";
  const h = Math.floor(m / 60);
  if (h < 24) return h + "h ago";
  const d = Math.floor(h / 24);
  return d + "d ago";
}
